/**
 * Dashboard.jsx — overview, matched to the web DashboardPage.
 *
 * ─── The mode toggle is the important part ──────────────────────────────────
 * The web defaults to LIVE and its stat rail carries the comment "fully
 * recomputed from mode-filtered trades (never mixed)".
 *
 * The first version of this screen summed live and paper together. On an
 * account with 120 live and 226 paper bots that produced a completely
 * different headline figure from the web — same account, same question, two
 * answers. Whichever was right, a user seeing both would conclude one was
 * broken.
 *
 * So: same default, same filtering, same labels. The four stats read
 * "{mode} P&L", "Today P&L", "Win rate", "{mode} bots running" exactly as
 * they do on the web, sub-lines included.
 *
 * ─── Why no equity chart ────────────────────────────────────────────────────
 * recharts is roughly the size of this whole bundle. The 7-day strip below is
 * drawn with divs — the web's own equity card degrades to "Not enough data to
 * chart" most of the time anyway.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import { C, fUsd, pnlColor, volTier, applyTier, Label, Panel, Button,
         Empty, Segment, Skeleton, SkeletonRow, TierBadge } from "../theme";
import { useCountUp, useFlash } from "../lib/motion";

export default function Dashboard({ onOpenBots, onOpen, onCreate, onDeposit, onAccount }) {
  // 'live' by default, exactly as the web does. Never mixed.
  const [mode, setMode] = useState("live");
  const [bots, setBots] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [trades, setTrades] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [b, w, t] = await Promise.all([
          api.listBots(),
          api.balance().catch(() => null),
          api.get("/api/user/trades", { limit: 200 }).catch(() => []),
        ]);
        setBots(Array.isArray(b) ? b : b?.bots || []);
        if (w) setWallet(w);
        setTrades(Array.isArray(t) ? t : t?.trades || []);
        setErr(null);
      } catch (e) { setErr(e.message); }
    };
    load();
    return api.poll(load, 10000);
  }, []);

  // Derived before any early return — the hooks below run every render.
  const all = bots || [];
  const modeBots = all.filter((b) => (b.mode || "").toLowerCase() === mode);
  const running = modeBots.filter((b) => b.status === "running");

  const totalPnl = modeBots.reduce(
    (s, b) => s + Number(b.total_profit_loss || 0), 0);
  const liveVol = all
    .filter((b) => (b.mode || "").toLowerCase() === "live")
    .reduce((s, b) => s + Number(b.total_volume || 0), 0);

  // Tier is driven by LIFETIME LIVE volume on the web, not total volume.
  const tier = volTier(liveVol).name;
  useEffect(() => { applyTier(tier); }, [tier]);

  const modeTrades = (trades || []).filter((t) => {
    const m = (t.mode || (String(t.buy_order_id || "").startsWith("paper") ? "paper" : "live"));
    return m === mode;
  });

  const todayKey = new Date().toISOString().slice(0, 10);
  const tradePnl = (t) => Number(t.total_profit_loss ?? t.profit_loss ?? 0);
  const tradeDay = (t) => String(
    t.sell_filled_at || t.closed_at || t.completed_at || t.timestamp || "").slice(0, 10);

  const settled = modeTrades.filter((t) => tradeDay(t)).length;
  const todayPnl = modeTrades
    .filter((t) => tradeDay(t) === todayKey)
    .reduce((s, t) => s + tradePnl(t), 0);

  const wins = modeTrades.filter((t) => tradePnl(t) > 0).length;
  const losses = modeTrades.filter((t) => tradePnl(t) < 0).length;
  const winRate = (wins + losses) ? (wins / (wins + losses)) * 100 : null;

  const bal = wallet
    ? Number(wallet.balance ?? wallet.usdc ?? wallet.available ?? 0)
    : null;

  const animPnl = useCountUp(totalPnl);
  const animBal = useCountUp(bal ?? 0);
  const flash = useFlash(totalPnl);

  const days = last7(modeTrades, tradePnl, tradeDay);
  const peak = Math.max(1, ...days.map((d) => Math.abs(d.pnl)));

  const best = [...modeBots]
    .filter((b) => Number(b.total_copied_trades || 0) > 0)
    .sort((a, b) => Number(b.total_profit_loss || 0) - Number(a.total_profit_loss || 0))
    .slice(0, 3);

  if (bots === null && !err) {
    return (
      <div style={{ padding: 14 }}>
        <Skeleton h={40} style={{ marginBottom: 12 }} />
        <Skeleton h={84} style={{ marginBottom: 12 }} />
        <Skeleton h={92} style={{ marginBottom: 12 }} />
        {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
      </div>
    );
  }
  if (err && !bots) return <Empty><span style={{ color: C.rose }}>{err}</span></Empty>;

  return (
    <div style={{ padding: 14 }}>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12, gap: 10 }}>
        <TierBadge tierName={tier} />
        <span style={{ fontSize: 10.5, color: C.dim }}>
          {fUsd(liveVol)} live volume
        </span>
      </div>

      {/* Never mixed — same rule as the web. */}
      <Segment
        value={mode}
        onChange={setMode}
        style={{ marginBottom: 12 }}
        options={[
          { value: "live",  label: "Live" },
          { value: "paper", label: "Paper" },
        ]}
      />

      {/* Nothing has settled in this mode: the stat rail would be four zeros
          and the chart an empty box. Say what the state actually is and what
          unblocks it instead. The rail returns as soon as there is data. */}
      {settled === 0 && (
        <div style={{ border: `1px solid ${C.line2}`, background: C.bg2,
                      padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.dim, letterSpacing: "0.16em",
                        marginBottom: 8 }}>
            {modeBots.length === 0 ? "NO BOTS YET" : "WAITING ON FIRST TRADE"}
          </div>
          <div style={{ fontFamily: C.disp, fontSize: 30, color: C.ink,
                        lineHeight: 1, marginBottom: 9 }}>
            {modeBots.length === 0
              ? `No ${mode} bots`
              : `${modeBots.length} ${mode} bot${modeBots.length === 1 ? "" : "s"}`}
          </div>
          <p style={{ fontSize: 11, color: C.sub, margin: 0, lineHeight: 1.7 }}>
            {modeBots.length === 0
              ? "Create one and it starts copying automatically."
              : running.length === 0
                ? "None are running. Start one from the bots list and it will copy the next position your trader opens."
                : "They copy the next position your trader opens. Nothing has settled yet."}
          </p>
        </div>
      )}

      {/* Live bots on a zero balance cannot place orders. That is the actual
          blocker, and it is worth naming before anything else on the screen. */}
      {mode === "live" && bal === 0 && modeBots.length > 0 && (
        <div style={{ border: `1px solid ${C.rose}55`,
                      background: "rgba(244,114,182,0.06)",
                      padding: 12, marginBottom: 14 }}>
          <p style={{ fontSize: 11.5, color: C.rose, margin: "0 0 4px" }}>
            Deposit needed
          </p>
          <p style={{ fontSize: 10.5, color: C.sub, margin: 0, lineHeight: 1.65 }}>
            Live bots cannot place orders on a $0 balance.
          </p>
        </div>
      )}

      {settled > 0 && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1,
                    background: C.line, border: `1px solid ${C.line}`,
                    marginBottom: 14 }}>
        <Stat lab={`${mode} P&L`} val={fUsd(animPnl, true)}
              color={totalPnl >= 0 ? C.green : C.rose}
              sub={`${settled} settled trades`} flash={flash} />
        <Stat lab="Today P&L" val={fUsd(todayPnl, true)}
              color={todayPnl >= 0 ? C.green : C.rose} sub={`${mode} mode`} />
        <Stat lab="Win rate"
              val={winRate != null ? `${Math.round(winRate)}%` : "—"}
              color={C.cyan}
              sub={winRate != null ? `${wins}W · ${losses}L` : "no settled trades"} />
        <Stat lab={`${mode} bots running`} val={running.length}
              sub={`${running.length} running · ${modeBots.length} ${mode} total`} />
      </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", border: `1px solid ${C.line}`,
                    background: C.bg1, padding: "11px 12px", marginBottom: 14 }}>
        <span style={{ fontSize: 11.5, color: C.dim }}>
          Balance <span style={{ color: C.ink }}>{bal === null ? "—" : fUsd(animBal)}</span>
        </span>
        <span onClick={onDeposit} className="pe-tap"
              style={{ fontSize: 11.5, color: C.amber, cursor: "pointer" }}>
          Deposit ›
        </span>
      </div>

      {days.some((d) => d.pnl !== 0) && (<>
      <Label>Last 7 days · {mode}</Label>
      <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                    padding: "14px 12px 10px", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6,
                      height: 56, marginBottom: 8 }}>
          {days.map((d, i) => (
            <div key={i} style={{ flex: 1, display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "flex-end", height: "100%" }}>
              <div style={{
                height: Math.max(2, (Math.abs(d.pnl) / peak) * 52),
                background: d.pnl === 0 ? C.bg3 : pnlColor(d.pnl),
                transition: "height 0.5s cubic-bezier(.22,1,.36,1)",
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {days.map((d, i) => (
            <span key={i} style={{ flex: 1, fontSize: 9, color: C.dim,
                                   textAlign: "center" }}>{d.label}</span>
          ))}
        </div>
      </div>
      </>)}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: 7, marginBottom: 18 }}>
        {mode === "live" && bal === 0 && modeBots.length > 0 ? (
          <>
            <Button onClick={onDeposit}>Deposit USDC</Button>
            <Button variant="ghost" onClick={onOpenBots}>
              View {modeBots.length} bots
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onCreate}>+ New bot</Button>
            <Button variant="ghost" onClick={onOpenBots}>All bots</Button>
          </>
        )}
      </div>

      {best.length > 0 && (
        <>
          <Label>Top {mode} performers</Label>
          {best.map((b) => {
            const pnl = Number(b.total_profit_loss || 0);
            return (
              <Panel key={b.bot_id} accent={pnlColor(pnl)} className="pe-row"
                     onClick={() => onOpen(b.bot_id)} style={{ marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "baseline", marginBottom: 5, gap: 8 }}>
                  <span style={{ fontSize: 13, color: C.ink, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.bot_name || b.trader_name || b.bot_id}
                  </span>
                  <span style={{ fontSize: 14, color: pnlColor(pnl),
                                 flexShrink: 0 }}>{fUsd(pnl, true)}</span>
                </div>
                <div style={{ display: "flex", gap: 8, fontSize: 11,
                              color: C.dim, flexWrap: "wrap" }}>
                  <span>{b.status}</span>
                  <span>· {b.total_copied_trades || 0} trades</span>
                  {b.win_rate ? <span>· {Number(b.win_rate).toFixed(0)}% win</span> : null}
                </div>
              </Panel>
            );
          })}
        </>
      )}

      {modeBots.length === 0 && (
        <Empty>
          No {mode} bots.
          <div style={{ marginTop: 16 }}>
            <Button onClick={onCreate}>Create one</Button>
          </div>
        </Empty>
      )}

      <div style={{ marginTop: 18 }}>
        <Button variant="quiet" onClick={onAccount}>Account &amp; fees</Button>
      </div>
    </div>
  );
}

const Stat = ({ lab, val, sub, color, flash }) => (
  <div style={{ background: C.bg1, padding: "14px 14px" }}>
    <div style={{ fontSize: 10, color: C.dim, letterSpacing: "0.16em",
                  textTransform: "uppercase", marginBottom: 7 }}>{lab}</div>
    <div className={flash || ""}
         style={{ fontFamily: C.disp, fontSize: 26, lineHeight: 1,
                  letterSpacing: "0.02em", color: color || C.ink }}>{val}</div>
    <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>{sub}</div>
  </div>
);

function last7(trades, pnlOf, dayOf) {
  const out = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    out.push({
      key: d.toISOString().slice(0, 10),
      label: ["S", "M", "T", "W", "T", "F", "S"][d.getDay()],
      pnl: 0,
    });
  }
  const index = Object.fromEntries(out.map((o) => [o.key, o]));
  for (const t of trades || []) {
    const k = dayOf(t);
    if (index[k]) index[k].pnl += pnlOf(t);
  }
  return out;
}
